import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { apiFetch, unwrapOne } from '@/utils/api';
import { useApp } from '@/components/hooks/useApp.jsx';
import { toastApiError } from '@/lib/query-client';

/**
 * Estado del panel "Primeros pasos" del Home. El backend deriva qué pasos están
 * hechos a partir de los datos reales de la empresa (ver
 * verix-api/src/functions/onboarding/onboardingStatus.ts), así que el frontend
 * solo lee el estado e invalida la query tras cada sub-flujo para re-derivar.
 */
export function useOnboardingStatus() {
  const { t } = useTranslation();
  const { company } = useApp();
  const queryClient = useQueryClient();
  const queryKey = ['onboarding-status', company?.id];

  const query = useQuery({
    queryKey,
    queryFn: async () => unwrapOne(await apiFetch('/api/onboarding/status')),
    enabled: !!company?.id,
    staleTime: 60 * 1000,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey });

  const dismiss = useMutation({
    mutationFn: () => apiFetch('/api/onboarding/dismiss', { method: 'POST' }),
    onSuccess: invalidate,
    onError: (error) => toastApiError(error, t('onboarding.dismiss_error', 'No se pudo ocultar el panel')),
  });

  const reopen = useMutation({
    mutationFn: () => apiFetch('/api/onboarding/reopen', { method: 'POST' }),
    onSuccess: () => {
      invalidate();
      toast.success(t('onboarding.reopen_success'));
    },
    onError: (error) => toastApiError(error, t('onboarding.reopen_error', 'No se pudo reactivar el panel')),
  });

  // "Lo haré luego" del flujo de bienvenida. Se persiste en la EMPRESA
  // (`settings.onboarding.welcome_skipped`) y no solo en el navegador: con el
  // marcador local, cambiar de dispositivo o abrir incógnito olvidaba el escape
  // y el gate volvía a atrapar al usuario en el flujo que ya había saltado.
  const skipWelcome = useMutation({
    mutationFn: () => apiFetch('/api/onboarding/welcome-skip', { method: 'POST' }),
    onSuccess: invalidate,
    // Sin toast de error a propósito: quien pulsa "Lo haré luego" se va de la
    // pantalla, y el marcador local (que WelcomeFlow escribe igualmente) ya
    // garantiza que el gate no lo devuelva. Avisar de un fallo que no puede
    // accionar solo añadiría ruido.
  });

  const ackStep = useMutation({
    mutationFn: (key) => apiFetch(`/api/onboarding/steps/${key}/ack`, { method: 'POST' }),
    onSuccess: invalidate,
    onError: (error) => toastApiError(error, t('onboarding.ack_error', 'No se pudo marcar el paso')),
  });

  return {
    status: query.data,
    isLoading: query.isLoading,
    dismiss,
    reopen,
    skipWelcome,
    ackStep,
    invalidate,
  };
}
